import "./homePage.css";
import React, { useEffect, useState } from "react";
import { icons } from "../../assets/icons";
import { videos } from "../../assets/video";
import { homepageImage } from "../../assets/images/homepageImg";
import { useTranslation } from "react-i18next";
import YouTube from "react-youtube";
import YoutubeVideo from "../../components/YoutubeVideo/YoutubeVideo";

function HomePage() {
  const { t, i18n } = useTranslation();

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const handleVideoEnd = () => {
    setIsVideoPlaying(false);
  };

  const onPlayerReady = (event) => {
    // access to player in all event handlers via event.target
    event.target.pauseVideo();
  };

  const opts = {
    height: "100%",
    width: "100%",
  };

  const produceItems = [
    {
      id: "bus",
      label: t("products_bus"),
      image: homepageImage.whatdowePng1,
    },
    {
      id: "minibus",
      label: t("products_minibus"),
      image: homepageImage.whatdowePng2,
    },
    {
      id: "truck",
      label: t("products_truck"),
      image: homepageImage.whatdowePng3,
    },
    {
      id: "aviation",
      label: t("products_aviation"),
      image: homepageImage.whatdowePng4,
    },
    {
      id: "tractor",
      label: t("products_tractor"),
      image: homepageImage.whatdowePng5,
    },
    {
      id: "electric",
      label: t("products_electric_vehicle"),
      image: homepageImage.whatdowePng6,
    },
  ];

  useEffect(() => {
    const elements = document.querySelectorAll(".reveal");
    if (!elements.length) {
      return undefined;
    }

    if (!("IntersectionObserver" in window)) {
      elements.forEach((el) => el.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const section = document.querySelector(".kpi-strip");
    if (!section) {
      return undefined;
    }

    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const formatNumber = (value) =>
      new Intl.NumberFormat(i18n.language).format(value);

    const numbers = Array.from(section.querySelectorAll(".kpi-number"));

    const setFinalValues = () => {
      numbers.forEach((element) => {
        const target = Number(element.dataset.target || 0);
        const suffix = element.dataset.suffix || "";
        element.textContent = `${formatNumber(target)}${suffix}`;
      });
    };

    const animateCount = (element) => {
      const target = Number(element.dataset.target || 0);
      const suffix = element.dataset.suffix || "";
      const duration = 1400;
      const start = performance.now();

      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const value = Math.round(progress * target);
        element.textContent = `${formatNumber(value)}${suffix}`;

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          element.textContent = `${formatNumber(target)}${suffix}`;
        }
      };

      requestAnimationFrame(step);
    };

    if (prefersReducedMotion) {
      setFinalValues();
      return undefined;
    }

    if (!("IntersectionObserver" in window)) {
      numbers.forEach(animateCount);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            numbers.forEach(animateCount);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, [i18n.language]);

  return (
    <div className="homepage-container">
      <header>
        <div className={`image-box ${isVideoPlaying ? "is-playing" : ""}`}>
          {isVideoPlaying ? (
            // <video
            // src={videos.payeplast_tanitim}
            // alt="video"
            // autoPlay
            // onEnded={handleVideoEnd}
            // />
            <YoutubeVideo autoplay={true} videoId={videos.payeplast_tanitim}  mute={false}/>
          ) : (
            <img src={homepageImage.headerPng} alt="header-img" />
          )}
        </div>
        {!isVideoPlaying && (
          <>
            <h1 className="header-text">
              {t("homepage_header_text1")} <br /> {t("homepage_header_text2")}
            </h1>
            <button
              onClick={() => setIsVideoPlaying(true)}
              className="watch-movie-btn"
            >
              <img src={icons.watchIcon} alt="icon" />
              <span>{t("btn_watch_movie")}</span>
            </button>
          </>
        )}

        <div className="social-media-links">
          <a
            href="https://wa.me/+905496165916"
            target="_blank"
            className="link-icon-box"
          >
            <img src={icons.wpGreenIcon} alt="icon" />
          </a>
          <a
            href="https://www.linkedin.com/company/paye-plast/"
            target="_blank"
            className="link-icon-box"
          >
            <img src={icons.linkedinBlueIcon} alt="icon" />
          </a>
        </div>
      </header>

      <section className="kpi-strip reveal" aria-label={t("homepage_kpi_title")}>
        <div className="kpi-inner">
          <div className="kpi-item">
            <span className="kpi-number" data-target="54">
              0
            </span>
            <span className="kpi-label">{t("homepage_kpi_countries")}</span>
          </div>
          <div className="kpi-item">
            <span className="kpi-number" data-target="6">
              0
            </span>
            <span className="kpi-label">{t("homepage_kpi_continents")}</span>
          </div>
          <div className="kpi-item">
            <span className="kpi-number" data-target="17000">
              0
            </span>
            <span className="kpi-label">{t("homepage_kpi_area")}</span>
          </div>
          <div className="kpi-item">
            <span className="kpi-number" data-target="190" data-suffix="+">
              0
            </span>
            <span className="kpi-label">{t("homepage_kpi_employees")}</span>
          </div>
        </div>
      </section>

      <div className="who-are-we">
        <div className="text reveal">
          <h3 className="title">{t("homepage_who_are_we")}</h3>
          <p>
            {t("homepage_who_are_we_text1")} <br />
            {t("homepage_who_are_we_text2")}{" "}
          </p>
          <br />
          <p>{t("homepage_who_are_we_text3")}</p>
          <br />
          <p>{t("homepage_who_are_we_text4")}</p>
          <br />
          <p>{t("homepage_who_are_we_text5")}</p>
        </div>

        <div className="images-content reveal delay-1">
          <div className="image-box">
            <img src={homepageImage.whoAreWe1Png} alt="image" />
          </div>
          <div className="image-box">
            <img src={homepageImage.whoAreWe2Png} alt="image" />
          </div>
        </div>
      </div>

      <div className="what-do-we-produce">
        <div className="text reveal">
          <h3 className="title">{t("homepage_what_we_produce")}</h3>
          <p>{t("homepage_what_we_produce_text1")}</p>

          <ul>
            <li>{t("homepage_what_we_produce_text2")}</li>
            <li>{t("homepage_what_we_produce_text3")}</li>
            <li>{t("homepage_what_we_produce_text4")}</li>
            <li>{t("homepage_what_we_produce_text5")}</li>
            <li>{t("homepage_what_we_produce_text6")}</li>
            <li>{t("homepage_what_we_produce_text7")}</li>
          </ul>
          <p>{t("homepage_what_we_produce_text8")}</p>
        </div>

        <div className="spotlight-grid reveal">
          {produceItems.map((item) => (
            <article className="spotlight-card" key={item.id}>
              <div className="spotlight-media">
                <img src={item.image} alt={item.label} />
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

export default HomePage;
/* WHAT ARE WE ESKİ HTML */
{
  /* <div className="text">
  <h3 className="title">{t("homepage_who_are_we")}</h3>
  <p>
    {t("about_text1")}
    <strong> {t("about_text2")}</strong> {t("about_text3")} {t("about_text4")},{" "}
    {t("about_text5")} <strong>{t("about_text6")}</strong> {t("about_text_and")}{" "}
    <strong>{t("about_text8")}</strong> {t("about_text9")}
  </p>
  <br />
  <p>
    {t("about_text10")} {t("about_text11")} <strong>{t("about_text12")}</strong>{" "}
    {t("about_text_and")} <strong>{t("about_text13")}</strong>{" "}
    {t("about_text14")} {t("about_text15")} {t("about_text16")}{" "}
    <strong>{t("about_text17")}</strong> {t("about_text18")}
  </p>
  <br />
  <p>
    <strong>{t("about_text19")}</strong> {t("about_text20")}
  </p>
  <br />
  <p>
    {t("about_text21")} <strong>{t("about_text22")}</strong>
    {t("about_text23")} <strong>{t("about_text24")}</strong> {t("about_text25")}
  </p>
</div>; */
}
